<template>
  <benchmark-group-table
    v-if="loading || props.sector"
    title="Industry Comparison"
    :has-subtitle="!!props.sector"
    :columns="COLUMNS"
    :groups="computedGroups"
    :loading="loading"
  >
    <template #subtitle>
      Comparing your metrics against the <strong>{{ props.sector }}</strong> sector average.
    </template>
  </benchmark-group-table>
</template>

<script setup>
import { computed } from 'vue';
import { INDUSTRY_COMPARISON_GROUPS } from '@/pages/analyticsBenchmark/benchmarkConfig';
import { buildMetricsMap, calculateDelta, formatValue } from '@/pages/analyticsBenchmark/benchmarkUtils';
import BenchmarkGroupTable from '@/pages/analyticsBenchmark/components/BenchmarkGroupTable.vue';

const COLUMNS = [
  { key: 'name', label: 'Metric' },
  { key: 'company', label: 'Your Company' },
  { key: 'sector', label: 'Sector Average' },
  { key: 'delta', label: 'Delta' },
];

const props = defineProps({
  metrics: { type: Array, default: () => [] },
  sector: { type: String, default: null },
  loading: { type: Boolean, default: false },
});

const metricsMap = computed(() => buildMetricsMap(props.metrics));

const computedGroups = computed(() =>
  INDUSTRY_COMPARISON_GROUPS.map(group => ({
    name: group.name,
    emoji: group.emoji,
    color: group.color,
    rows: group.metrics.map(config => {
      const metric = config.metricId ? metricsMap.value[config.metricId] : null;
      const companyValue = metric?.company_value ?? null;
      const sectorValue = metric?.sector_value ?? null;
      const unit = metric?.unit || 'percent';
      const delta = config.metricId
        ? calculateDelta(companyValue, sectorValue, {
            deltaFormat: config.deltaFormat,
            polarity: config.polarity,
            unit,
          })
        : { raw: null, formatted: 'TBC', colorClass: 'delta-neutral' };

      return {
        id: config.metricId || config.label,
        label: config.label,
        description: metric?.description || null,
        tooltipId: `ic-${(config.metricId || config.label).replace(/\s+/g, '-')}`,
        col1: config.metricId ? formatValue(companyValue, unit) : 'TBC',
        col2: config.metricId ? formatValue(sectorValue, unit) : 'TBC',
        col2Label: null,
        deltaFormatted: delta.formatted,
        deltaColorClass: delta.colorClass,
      };
    }),
  })),
);
</script>
